import { useEffect, useState } from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link, useNavigate } from "@tanstack/react-router";
import { ChevronDownIcon, UserPen, Users } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "react-toastify";
import type { CombinedUser } from "@/lib/collections/workerProfile-Users/getUserWithProfile";
import { updateWorkerProfile } from "@/lib/collections/workerProfile-Users/updateWorkerProfile";
import { ComboBoxUserSelect, type UserOption } from "@/components/ui/combo-box";
import { fetchUsersWithProfiles } from "@/lib/collections/workerProfile-Users/fetchCollaboratorsWithProfile";
import { ComboBoxDepartmentSelect, type DepartmentOption } from "@/components/ui/combo-box-department";
import { getDepartments, regenerateColabCode, type Department } from "./service/colabCorpCode_generator";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { Option } from "@/components/ui/multiple-selector";
import { GenericListManager } from "@/components/ui/generic-list-manager";
import { Switch } from "@/components/ui/switch";
import { getDepartmentLabel } from "./department/constantes-departamentos/departamentos";

type EditCollaboratorProps = {
  user: CombinedUser;
};

const EditCollaborator = ({ user }: EditCollaboratorProps) => {
  const [openBirthday, setOpenBirthday] = useState(false);
  const [dateBirthday, setDateBirthday] = useState<Date | undefined>(
    user.profile?.birthday ? new Date(user.profile.birthday) : undefined
  );
  const [openJoinDate, setOpenJoinDate] = useState(false);
  const [dateJoinDate, setDateJoinDate] = useState<Date | undefined>(
    user.profile?.joinDate ? new Date(user.profile.joinDate) : undefined
  );
  const [openLeaveDate, setOpenLeaveDate] = useState(false);
  const [dateLeaveDate, setDateLeaveDate] = useState<Date | undefined>(
    user.profile?.leaveDate ? new Date(user.profile.leaveDate) : undefined
  );

  const [loading, setLoading] = useState(false);
  const [supervisores, setSupervisores] = useState<UserOption[]>([]);
  const [supLoading, setSupLoading] = useState(true);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [loadingDepartments, setLoadingDepartments] = useState(true);
  const [_keyFunctionOptions, setKeyFunctionOptions] = useState<Option[]>([]);

  const navigate = useNavigate();

  const [form, setForm] = useState({
    // Nombre separado
    firstName: user.profile?.firstName ?? "",
    middleName: user.profile?.middleName ?? "",
    lastName: user.profile?.lastName ?? "",
    secondLastName: user.profile?.secondLastName ?? "",
    // Acceso (email read-only en edit, no se puede cambiar desde aquí)
    email: user.email,
    // Personales
    birthday: user.profile?.birthday ?? "",
    bloodType: user.profile?.bloodType ?? "",
    colabDniCode: user.profile?.colabDniCode ?? "",
    phone1: user.profile?.phone1 ?? "",
    phone2: user.profile?.phone2 ?? "",
    emergencyContact: user.profile?.emergencyContact ?? "",
    address: user.profile?.address ?? "",
    // Corporativos
    joinDate: user.profile?.joinDate ?? "",
    rolCorp: user.profile?.rolCorp ?? "",
    occupation: user.profile?.occupation ?? "",
    occupationAbbreviation: user.profile?.occupationAbbreviation ?? "",
    colabCorpCode: user.profile?.colabCorpCode ?? "",
    area: user.profile?.area ?? "",
    idSupervisor: user.profile?.idSupervisor ?? "",
    departmentId: user.profile?.departmentId ?? "",
    isActive: user.profile?.isActive ?? false,
    leaveDate: user.profile?.leaveDate ?? "",
    // Bancarios
    bank: user.profile?.bank ?? "",
    bankAccountNumber: user.profile?.bankAccountNumber ?? "",
    bankAccountType: user.profile?.bankAccountType ?? "",
    // Otros
    keyFunctions: user.profile?.keyFunctions ?? "",
    keyFunction: user.profile?.keyFunction ?? [],
    archievements: user.profile?.archievements ?? "",
    archievement: user.profile?.archievement ?? [],
  });

  // Reinicia el form cuando cambia el user
  useEffect(() => {
    setForm({
      firstName: user.profile?.firstName ?? "",
      middleName: user.profile?.middleName ?? "",
      lastName: user.profile?.lastName ?? "",
      secondLastName: user.profile?.secondLastName ?? "",
      email: user.email,
      birthday: user.profile?.birthday ?? "",
      bloodType: user.profile?.bloodType ?? "",
      colabDniCode: user.profile?.colabDniCode ?? "",
      phone1: user.profile?.phone1 ?? "",
      phone2: user.profile?.phone2 ?? "",
      emergencyContact: user.profile?.emergencyContact ?? "",
      address: user.profile?.address ?? "",
      joinDate: user.profile?.joinDate ?? "",
      rolCorp: user.profile?.rolCorp ?? "",
      occupation: user.profile?.occupation ?? "",
      occupationAbbreviation: user.profile?.occupationAbbreviation ?? "",
      colabCorpCode: user.profile?.colabCorpCode ?? "",
      area: user.profile?.area ?? "",
      idSupervisor: user.profile?.idSupervisor ?? "",
      departmentId: user.profile?.departmentId ?? "",
      isActive: user.profile?.isActive ?? false,
      leaveDate: user.profile?.leaveDate ?? "",
      bank: user.profile?.bank ?? "",
      bankAccountNumber: user.profile?.bankAccountNumber ?? "",
      bankAccountType: user.profile?.bankAccountType ?? "",
      keyFunctions: user.profile?.keyFunctions ?? "",
      keyFunction: user.profile?.keyFunction ?? [],
      archievements: user.profile?.archievements ?? "",
      archievement: user.profile?.archievement ?? [],
    });
    setDateBirthday(user.profile?.birthday ? new Date(user.profile.birthday) : undefined);
    setDateJoinDate(user.profile?.joinDate ? new Date(user.profile.joinDate) : undefined);
    setDateLeaveDate(user.profile?.leaveDate ? new Date(user.profile.leaveDate) : undefined);
  }, [user]);

  useEffect(() => {
    setSupLoading(true);
    fetchUsersWithProfiles()
      .then(list => {
        const opts = list.map(u => ({ id: u.id, name: u.name }));
        setSupervisores(opts);
        if (user.profile?.idSupervisor) {
          const exists = opts.some(o => o.id === user.profile!.idSupervisor);
          if (exists) setForm(f => ({ ...f, idSupervisor: user.profile!.idSupervisor! }));
        }
      })
      .catch(console.error)
      .finally(() => setSupLoading(false));
  }, [user.profile?.idSupervisor]);

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const depts = await getDepartments();
        const formattedDepts: DepartmentOption[] = depts.map((dept: Department) => ({
          id: dept.$id,
          name: getDepartmentLabel(dept.departmentName),
          number: dept.departmentNumber,
        }));
        setDepartments(formattedDepts);
      } catch (error) {
        console.error("Error cargando departamentos:", error);
      } finally {
        setLoadingDepartments(false);
      }
    };
    loadDepartments();
  }, []);

  useEffect(() => {
    if (form.departmentId && form.birthday && departments.length > 0) {
      const selectedDept = departments.find(d => d.id === form.departmentId);
      if (selectedDept && form.colabCorpCode) {
        const newCode = regenerateColabCode(selectedDept.number, form.birthday, form.colabCorpCode);
        if (newCode !== form.colabCorpCode) {
          setForm((prev) => ({ ...prev, colabCorpCode: newCode }));
        }
      }
    }
  }, [form.departmentId, form.birthday, departments, form.colabCorpCode]);

  useEffect(() => {
    if (form.keyFunction && form.keyFunction.length > 0) {
      const options: Option[] = form.keyFunction.map(func => ({ value: func, label: func }));
      setKeyFunctionOptions(options);
    }
  }, [form.keyFunction]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { name: string; value: any } }
  ) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectBirthday = (selectedDate: Date | undefined) => {
    setDateBirthday(selectedDate);
    if (selectedDate) setForm((prev) => ({ ...prev, birthday: selectedDate.toISOString() }));
    setOpenBirthday(false);
  };

  const handleSelectJoinDate = (selectedDate: Date | undefined) => {
    setDateJoinDate(selectedDate);
    if (selectedDate) setForm((prev) => ({ ...prev, joinDate: selectedDate.toISOString() }));
    setOpenJoinDate(false);
  };

  const handleSelectLeaveDate = (selectedDate: Date | undefined) => {
    setDateLeaveDate(selectedDate);
    if (selectedDate) setForm((prev) => ({ ...prev, leaveDate: selectedDate.toISOString() }));
    setOpenLeaveDate(false);
  };

  const handleDepartmentChange = (departmentId: string) => {
    setForm((prev) => ({ ...prev, departmentId }));
  };

  const handleSubmit = async () => {
    if (loading) return;
    setLoading(true);
    try {
      if (!user.profile) {
        toast.error("Perfil del colaborador no encontrado.", { autoClose: 5000 });
        setLoading(false);
        return;
      }
      await updateWorkerProfile(user.id, user.profile.$id, form);
      toast.success("Colaborador actualizado con éxito", { autoClose: 5000 });
    } catch (error: any) {
      console.error(error);
      toast.error("Error al actualizar colaborador.", { autoClose: 5000 });
      setTimeout(() => navigate({ to: "/directory" }), 5000);
    } finally {
      setLoading(false);
    }
  };

  const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

  // Nombre completo para mostrar en el header (igual que auth)
  const displayName = [form.firstName, form.middleName, form.lastName, form.secondLastName]
    .map(s => s.trim()).filter(Boolean).join(" ") || user.name;

  return (
    <>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <Users />
            <BreadcrumbLink asChild>
              <BreadcrumbPage>Directorio</BreadcrumbPage>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbLink asChild>
            <Link to="/directory">Colaboradores</Link>
          </BreadcrumbLink>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className="hidden sm:block">Editar colaborador</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="px-4 sm:px-6 lg:px-8 py-6 font-outfit text-center">
        <Card className="shadow-lg">
          <CardContent className="flex justify-center gap-2 py-4">
            <UserPen />
            <Label className="text-xl">
              Editar Colaborador <span className="text-yellow-600">{displayName}</span>
            </Label>
          </CardContent>
        </Card>

        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <div className="py-5 font-outfit flex flex-col gap-5">

            {/* ── Datos personales ── */}
            <Card className="shadow-lg">
              <CardContent className="py-6 text-left">
                <p className={sectionTitleStyle}>Datos personales</p>
                <div className="grid grid-cols-2 gap-4">

                  <div className={divStyle}>
                    <Label htmlFor="firstName" className={labelStyle}>Primer nombre:</Label>
                    <Input id="firstName" name="firstName" value={form.firstName} onChange={handleChange} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="middleName" className={labelStyle}>Segundo nombre:</Label>
                    <Input id="middleName" name="middleName" value={form.middleName} onChange={handleChange} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="lastName" className={labelStyle}>Primer apellido:</Label>
                    <Input id="lastName" name="lastName" value={form.lastName} onChange={handleChange} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="secondLastName" className={labelStyle}>Segundo apellido:</Label>
                    <Input id="secondLastName" name="secondLastName" value={form.secondLastName} onChange={handleChange} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="colabDniCode" className={labelStyle}>Número de cédula:</Label>
                    <Input id="colabDniCode" name="colabDniCode" value={form.colabDniCode} onChange={handleChange} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="bloodType" className={labelStyle}>Tipo de sangre:</Label>
                    <Select value={form.bloodType} onValueChange={(value) => setForm(prev => ({ ...prev, bloodType: value }))}>
                      <SelectTrigger className="w-full border-gray-300 dark:border-gray-600">
                        <SelectValue placeholder="Selecciona el tipo de sangre" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {BLOOD_TYPES.map(type => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="birthday" className={labelStyle}>Fecha de nacimiento:</Label>
                    <Popover open={openBirthday} onOpenChange={setOpenBirthday}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" id="birthday" className="w-full justify-between font-outfit text-gray-500" type="button">
                          {dateBirthday ? dateBirthday.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : "Seleccionar fecha"}
                          <ChevronDownIcon />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto overflow-hidden p-0" align="start">
                        <Calendar mode="single" selected={dateBirthday} captionLayout="dropdown" defaultMonth={dateBirthday} onSelect={handleSelectBirthday} className="text-gray-400" />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="address" className={labelStyle}>Dirección:</Label>
                    <Textarea id="address" name="address" value={form.address} onChange={handleChange} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="phone1" className={labelStyle}>Teléfono 1:</Label>
                    <Input id="phone1" name="phone1" value={form.phone1} onChange={handleChange} maxLength={15} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="phone2" className={labelStyle}>Teléfono 2:</Label>
                    <Input id="phone2" name="phone2" value={form.phone2} onChange={handleChange} maxLength={15} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="emergencyContact" className={labelStyle}>Contacto de emergencia:</Label>
                    <Input id="emergencyContact" name="emergencyContact" value={form.emergencyContact} onChange={handleChange} maxLength={15} className={inputStyle} />
                  </div>

                </div>
              </CardContent>
            </Card>

            {/* ── Datos corporativos ── */}
            <Card className="shadow-lg">
              <CardContent className="py-6 text-left">
                <p className={sectionTitleStyle}>Datos corporativos</p>
                <div className="grid grid-cols-2 gap-4">

                  <div className={divStyle}>
                    <Label htmlFor="rolCorp" className={labelStyle}>Rol corporativo:</Label>
                    <Input id="rolCorp" name="rolCorp" value={form.rolCorp} onChange={handleChange} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="occupation" className={labelStyle}>Ocupación / Profesión:</Label>
                    <Input id="occupation" name="occupation" value={form.occupation} onChange={handleChange} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="occupationAbbreviation" className={labelStyle}>Abreviatura de ocupación:</Label>
                    <Input id="occupationAbbreviation" name="occupationAbbreviation" value={form.occupationAbbreviation} onChange={handleChange} maxLength={20} className={inputStyle} placeholder="Ej: Ing., Dr., Lic." />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="area" className={labelStyle}>Área:</Label>
                    <Input id="area" name="area" value={form.area} onChange={handleChange} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="departmentId" className={labelStyle}>Departamento:</Label>
                    {loadingDepartments ? (
                      <Input value="Cargando..." disabled className={inputStyle} />
                    ) : (
                      <ComboBoxDepartmentSelect departments={departments} value={form.departmentId} placeholder="Seleccionar dep.." onChange={handleDepartmentChange} />
                    )}
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="colabCorpCode" className={labelStyle}>Código de colaborador:</Label>
                    <Input id="colabCorpCode" name="colabCorpCode" value={form.colabCorpCode} className={inputStyle} readOnly />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="idSupervisor" className={labelStyle}>Superior inmediato:</Label>
                    {supLoading ? (
                      <p className="text-gray-300 text-sm">Cargando…</p>
                    ) : (
                      <ComboBoxUserSelect users={supervisores} value={form.idSupervisor} onChange={id => setForm(f => ({ ...f, idSupervisor: id }))} />
                    )}
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="joinDate" className={labelStyle}>Fecha de ingreso:</Label>
                    <Popover open={openJoinDate} onOpenChange={setOpenJoinDate}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" id="joinDate" className="w-full justify-between font-outfit text-gray-500" type="button">
                          {dateJoinDate ? dateJoinDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : "Seleccionar fecha"}
                          <ChevronDownIcon />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto overflow-hidden p-0" align="start">
                        <Calendar mode="single" selected={dateJoinDate} defaultMonth={dateJoinDate} captionLayout="dropdown" onSelect={handleSelectJoinDate} className="text-gray-400" />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="isActive" className={labelStyle}>Estado del colaborador:</Label>
                    <div className="flex items-center gap-3">
                      <Switch
                        id="isActive"
                        name="isActive"
                        checked={form.isActive}
                        onCheckedChange={(checked) => handleChange({ target: { name: "isActive", value: checked } })}
                      />
                      <span className={`text-sm font-medium ${form.isActive ? "text-green-600" : "text-red-500"}`}>
                        {form.isActive ? "Activo" : "Inactivo"}
                      </span>
                    </div>
                  </div>

                  {!form.isActive && (
                    <div className={divStyle}>
                      <Label htmlFor="leaveDate" className={labelStyle}>Fecha de salida:</Label>
                      <Popover open={openLeaveDate} onOpenChange={setOpenLeaveDate}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" id="leaveDate" className="w-full justify-between font-outfit text-gray-500" type="button">
                            {dateLeaveDate ? dateLeaveDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : "Seleccionar fecha"}
                            <ChevronDownIcon />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
                          <Calendar mode="single" selected={dateLeaveDate} defaultMonth={dateLeaveDate} captionLayout="dropdown" onSelect={handleSelectLeaveDate} className="text-gray-400" />
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}

                </div>
              </CardContent>
            </Card>

            {/* ── Acceso ── */}
            <Card className="shadow-lg">
              <CardContent className="py-6 text-left">
                <p className={sectionTitleStyle}>Acceso a la plataforma</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className={divStyle}>
                    <Label htmlFor="email" className={labelStyle}>Correo electrónico:</Label>
                    <Input id="email" name="email" type="email" value={form.email} disabled className={inputStyle} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ── Datos bancarios ── */}
            <Card className="shadow-lg">
              <CardContent className="py-6 text-left">
                <p className={sectionTitleStyle}>Datos bancarios</p>
                <div className="grid grid-cols-2 gap-4">

                  <div className={divStyle}>
                    <Label htmlFor="bank" className={labelStyle}>Banco:</Label>
                    <Input id="bank" name="bank" value={form.bank} onChange={handleChange} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="bankAccountNumber" className={labelStyle}>Número de cuenta:</Label>
                    <Input id="bankAccountNumber" name="bankAccountNumber" value={form.bankAccountNumber} onChange={handleChange} className={inputStyle} />
                  </div>

                  <div className={divStyle}>
                    <Label htmlFor="bankAccountType" className={labelStyle}>Tipo de cuenta:</Label>
                    <Input id="bankAccountType" name="bankAccountType" value={form.bankAccountType} onChange={handleChange} className={inputStyle} />
                  </div>

                </div>
              </CardContent>
            </Card>

            {/* ── Funciones y logros ── */}
            <Card className="shadow-lg">
              <CardContent className="py-6 text-left">
                <p className={sectionTitleStyle}>Funciones y logros</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={divStyle}>
                    <GenericListManager
                      items={form.archievement}
                      onChange={(items) => setForm(prev => ({ ...prev, archievement: items }))}
                      label="Logros"
                      placeholder="Describe el logro del colaborador..."
                      singularLabel="Nuevo logro"
                      pluralLabel="logros"
                    />
                  </div>
                  <div className={divStyle}>
                    <GenericListManager
                      items={form.keyFunction}
                      onChange={(items) => setForm(prev => ({ ...prev, keyFunction: items }))}
                      label="Funciones clave"
                      placeholder="Describe la función clave del colaborador..."
                      singularLabel="Nueva función"
                      pluralLabel="funciones"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ── Botones ── */}
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 px-4 py-2">
              <Button type="submit" disabled={loading} className="bg-blue-500 text-white rounded-2xl w-full sm:w-48 cursor-pointer">
                {loading ? "Guardando..." : "Guardar cambios"}
              </Button>
              <Button type="button" className="bg-red-400 text-white rounded-2xl w-full sm:w-48 cursor-pointer" onClick={() => navigate({ to: "/directory" })}>
                Cancelar
              </Button>
            </div>

          </div>
        </form>
      </div>
    </>
  );
};

const divStyle = "flex flex-col gap-1.5 mx-1";
const labelStyle = "pl-3 text-gray-600";
const inputStyle = "border-gray-300 dark:border-gray-600";
const sectionTitleStyle = "text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 pl-1";

export default EditCollaborator;